import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  associateSupplierCatalogProduct,
  listSupplierCatalogPage,
  listSupplierCatalogProducts,
  removeSupplierCatalogProduct,
} from "./api";
import type {
  SupplierCatalogAssociatePayload,
  SupplierCatalogPageQuery,
} from "./types";

export const SUPPLIER_CATALOG_QUERY_KEY = ["supplier-catalog-products"];
export const SUPPLIER_CATALOG_QUERY_LIMIT = 2000;

export function useSupplierCatalogProducts() {
  return useQuery({
    queryKey: SUPPLIER_CATALOG_QUERY_KEY,
    queryFn: () => listSupplierCatalogProducts(SUPPLIER_CATALOG_QUERY_LIMIT, 0),
  });
}

function normalizeSupplierCatalogPageQuery(
  query: SupplierCatalogPageQuery,
): SupplierCatalogPageQuery {
  const normalized = {
    limit: query.limit,
    offset: query.offset,
    search: query.search.trim(),
    mode: query.mode,
  };

  return query.category
    ? { ...normalized, category: query.category }
    : normalized;
}

export function useSupplierCatalogPage(
  query: SupplierCatalogPageQuery,
  enabled = true,
) {
  const normalizedQuery = normalizeSupplierCatalogPageQuery(query);

  return useQuery({
    queryKey: [...SUPPLIER_CATALOG_QUERY_KEY, "page", normalizedQuery],
    queryFn: ({ signal }) => listSupplierCatalogPage(normalizedQuery, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useAssociateSupplierCatalogProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SupplierCatalogAssociatePayload) =>
      associateSupplierCatalogProduct(payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SUPPLIER_CATALOG_QUERY_KEY }),
  });
}

export function useRemoveSupplierCatalogProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => removeSupplierCatalogProduct(productId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: SUPPLIER_CATALOG_QUERY_KEY }),
  });
}
